import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { ApiError, api, DELING_PAA_PAUSE } from '../api/client';
import type { Plan, User, WeddingSummary } from '../api/types';
import { gjeldendePlan } from '../lib/plans';
import {
  brukOppLanding,
  byttBesoksId,
  glemAktivKonto,
  innloggingsfelter,
  kampanjefelter,
  kobleBesoksId,
} from '../lib/trakt';

const ACTIVE_WEDDING_KEY = 'vaardag.activeWedding';

type SessionResponse = {
  user: User;
  weddings: WeddingSummary[];
  /** Besøks-ID-en hører til en annen konto. Se `byttBesoksId`. */
  byttBesoksId?: boolean;
};

type AuthState = {
  status: 'loading' | 'authenticated' | 'anonymous';
  user: User | null;
  weddings: WeddingSummary[];
  activeWedding: WeddingSummary | null;
  login: (email: string, password: string) => Promise<SessionResponse>;
  register: (input: {
    name: string;
    email: string;
    password: string;
    acceptTerms: boolean;
    marketingConsent: boolean;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refresh: () => Promise<SessionResponse | null>;
  selectWedding: (weddingId: string) => void;
  /**
   * Oppdaterer planen lokalt etter en gjennomført oppgradering.
   *
   * Tar det som er betalt for. Løper gratisperioden fortsatt, beholder de Plus
   * ut perioden – også om de nettopp kjøpte Standard.
   */
  setPlan: (weddingId: string, plan: Plan) => void;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthState['status']>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [weddings, setWeddings] = useState<WeddingSummary[]>([]);
  const [activeId, setActiveId] = useState<string | null>(() =>
    localStorage.getItem(ACTIVE_WEDDING_KEY),
  );

  const applySession = useCallback((session: SessionResponse) => {
    // Hører besøks-ID-en til noen andre, får nettleseren en ny. Se
    // `kobleBesoksId` – den dekker også økter som startet utenfor
    // innloggingen, som partnerinvitasjonen.
    if (session.byttBesoksId) byttBesoksId();
    kobleBesoksId(session.user.id);
    setUser(session.user);
    setWeddings(session.weddings);
    setStatus('authenticated');
    setActiveId((current) => {
      const stillValid = session.weddings.some((wedding) => wedding.id === current);
      if (stillValid) return current;
      // Et bryllup der delingen er på pause, viser ingenting. Har man et annet,
      // er det et bedre sted å lande.
      const forst =
        session.weddings.find((wedding) => !wedding.delingPaaPause) ?? session.weddings[0];
      return forst?.id ?? null;
    });
  }, []);

  const refresh = useCallback(async () => {
    try {
      const session = await api.get<SessionResponse>('/auth/me');
      applySession(session);
      return session;
    } catch (error) {
      if (error instanceof ApiError && error.isUnauthorized) {
        glemAktivKonto();
        setUser(null);
        setWeddings([]);
        setStatus('anonymous');
        return null;
      }
      setStatus('anonymous');
      throw error;
    }
  }, [applySession]);

  useEffect(() => {
    void refresh().catch(() => {
      /* Feil vises der brukeren faktisk prøver å gjøre noe. */
    });
  }, [refresh]);

  // Delingen ble satt på pause mens siden sto åpen. Flere sider spør samtidig,
  // så det holder å hente økta én gang i slengen.
  useEffect(() => {
    let sist = 0;
    const paaPause = () => {
      if (Date.now() - sist < 10_000) return;
      sist = Date.now();
      void refresh().catch(() => undefined);
    };
    window.addEventListener(DELING_PAA_PAUSE, paaPause);
    return () => window.removeEventListener(DELING_PAA_PAUSE, paaPause);
  }, [refresh]);

  useEffect(() => {
    if (activeId) localStorage.setItem(ACTIVE_WEDDING_KEY, activeId);
    else localStorage.removeItem(ACTIVE_WEDDING_KEY);
  }, [activeId]);

  const login = useCallback(
    async (email: string, password: string) => {
      // Økta gis tilbake, ikke bare lagret: innloggingssida må vite om
      // brukeren har flere bryllup å velge mellom før den navigerer videre,
      // og konteksten er ikke oppdatert ennå i samme tikk.
      //
      // Siden fanen landet på følger med, så trakten kan skille kunder på vei
      // inn fra folk som ser seg om. Se `innloggingsfelter`.
      const session = await api.post<SessionResponse>('/auth/login', {
        email,
        password,
        ...innloggingsfelter(),
      });
      brukOppLanding();
      applySession(session);
      return session;
    },
    [applySession],
  );

  const register = useCallback(
    async (input: {
      name: string;
      email: string;
      password: string;
      acceptTerms: boolean;
      marketingConsent: boolean;
    }) => {
      // Hvor de kom fra følger med inn i registreringen. Det er her koblingen
      // mellom en anonym besøkende og en kunde blir til – og den festes til
      // kontoen, ikke til nettleseren.
      //
      // Klikk-ID-en er med bare når den besøkende har sagt ja til annonsemåling.
      // Se `lib/trakt.ts`; serveren håndhever det samme, uansett hva som sendes.
      const session = await api.post<SessionResponse>('/auth/register', {
        ...input,
        ...kampanjefelter(),
      });
      applySession(session);
    },
    [applySession],
  );

  const logout = useCallback(async () => {
    await api.post('/auth/logout');
    brukOppLanding();
    glemAktivKonto();
    setUser(null);
    setWeddings([]);
    setActiveId(null);
    setStatus('anonymous');
  }, []);

  const setPlan = useCallback((weddingId: string, plan: Plan) => {
    setWeddings((current) =>
      current.map((wedding) => {
        if (wedding.id !== weddingId) return wedding;
        const gjeldende = gjeldendePlan(plan, wedding.plusProveTil);
        return {
          ...wedding,
          plan: gjeldende,
          // Har de kjøpt Plus, er det kjøpet som gir dem pakken, og da er det
          // ingen gratisperiode igjen å vise fram.
          plusProveTil: gjeldende === plan ? null : wedding.plusProveTil,
        };
      }),
    );
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      weddings,
      activeWedding: weddings.find((wedding) => wedding.id === activeId) ?? null,
      login,
      register,
      logout,
      refresh,
      selectWedding: setActiveId,
      setPlan,
    }),
    [status, user, weddings, activeId, login, register, logout, refresh, setPlan],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth må brukes innenfor AuthProvider.');
  return context;
}
